import React, { FunctionComponent, Children, isValidElement } from 'react';
import classNames from 'classnames';

import { CKTextFieldVariant } from '../../common/types';
import {
  CKTextField,
  CKMultilineTextField,
  SuccessIcon,
  ErrorIcon,
} from '../CKTextField';

export interface CKFieldProps {
  className?: string;
  children: React.ReactNode;
  variant?: CKTextFieldVariant;
  isSmall?: boolean;
}

export interface CKFieldElementProps {
  children: React.ReactNode;
  isInteractive?: boolean;
}

const CKFieldLeftElement: FunctionComponent<CKFieldElementProps> = ({
  children,
  isInteractive,
}) => {
  return (
    <span
      className="ck-field-icon ck-field-icon--left"
      style={{
        pointerEvents: isInteractive ? 'all' : 'none',
      }}
    >
      {children}
    </span>
  );
};

const CKFieldRightElement: FunctionComponent<CKFieldElementProps> = ({
  children,
  isInteractive,
}) => {
  return (
    <span
      className="ck-field-icon ck-field-icon--right"
      style={{
        pointerEvents: isInteractive ? 'all' : 'none',
      }}
    >
      {children}
    </span>
  );
};

export const CKField: FunctionComponent<CKFieldProps> = ({
  className,
  children,
  variant = CKTextFieldVariant.default,
  isSmall,
}) => {
  let iconModifierClasses: string[] = [];
  Children.forEach(children, (child: React.ReactNode) => {
    if (!isValidElement(child)) return;
    if (child.type === CKFieldLeftElement) {
      iconModifierClasses.push('ck-field--icon-left');
    }
    if (child.type === CKFieldRightElement) {
      iconModifierClasses.push('ck-field--icon-right');
    }
    return;
  });

  const wrapperClass = classNames(
    `ck-field`,
    ...iconModifierClasses,
    {
      [`ck-field--${variant}`]: variant !== CKTextFieldVariant.default,
      'ck-field--small': isSmall,
    },
    className
  );

  if (
    variant === CKTextFieldVariant.success ||
    variant === CKTextFieldVariant.error
  ) {
    return (
      <div
        className={classNames(
          `ck-field ck-field--${variant} ck-field--icon-right`,
          className
        )}
      >
        {Children.map(children, (child: React.ReactNode) => {
          if (!isValidElement(child)) return;
          if (child.type === CKTextField || child.type === CKMultilineTextField)
            return child;
          return null;
        })}
        <CKFieldRightElement>
          {variant === CKTextFieldVariant.success && SuccessIcon}
          {variant === CKTextFieldVariant.error && ErrorIcon}
        </CKFieldRightElement>
      </div>
    );
  } else {
    return <div className={wrapperClass}>{children}</div>;
  }
};

export { CKFieldLeftElement, CKFieldRightElement };
